import { useEffect, useState } from 'react';
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
    RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
    ResponsiveContainer, BarChart, Bar, Cell,
} from 'recharts';
import {
    RiLineChartLine, RiBarChartBoxLine, RiFireLine,
    RiArrowUpLine, RiArrowDownLine, RiTimeLine,
    RiRobot2Line, RiRefreshLine, RiTrophyLine,
    RiAlertLine, RiLightbulbLine, RiCheckboxCircleLine,
    RiArrowRightLine, RiSparklingLine,
} from 'react-icons/ri';
import {
    analyticsService,
    DIM_LABELS,
    type PerformanceAnalytics,
    type PerformancePoint,
    type DimensionScores,
} from '../../services/analyticsService';
import GlassCard from '../../components/ui/v2/GlassCard';

// ── Helpers ─────────────────────────────────────────────────────────────────

const RANGE_OPTIONS = [
    { label: '7d',  days: 7 },
    { label: '30d', days: 30 },
    { label: '90d', days: 90 },
    { label: 'All', days: undefined },
] as const;

function scoreColor(score: number) {
    if (score >= 80) return 'text-emerald-400';
    if (score >= 60) return 'text-yellow-400';
    return 'text-red-400';
}

function scoreBg(score: number) {
    if (score >= 80) return 'bg-emerald-500/10 border-emerald-500/20';
    if (score >= 60) return 'bg-yellow-500/10 border-yellow-500/20';
    return 'bg-red-500/10 border-red-500/20';
}

function formatDate(iso: string) {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function dimLabel(key: string) {
    return DIM_LABELS[key as keyof typeof DIM_LABELS] ?? key;
}

// ── Stat Card ───────────────────────────────────────────────────────────────

function StatCard({
    icon, label, value, sub, positive,
}: {
    icon: React.ReactNode;
    label: string;
    value: string;
    sub?: string;
    positive?: boolean | null;
}) {
    return (
        <GlassCard className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
                <p className="text-white/40 text-xs font-medium uppercase tracking-wider">{label}</p>
                <div className="w-8 h-8 rounded-lg bg-white/[0.05] border border-white/[0.08] flex items-center justify-center text-white/50">
                    {icon}
                </div>
            </div>
            <div>
                <p className={`text-3xl font-black ${positive === true ? 'text-emerald-400' : positive === false ? 'text-red-400' : 'text-white'}`}>
                    {value}
                </p>
                {sub && <p className="text-white/30 text-xs mt-1">{sub}</p>}
            </div>
        </GlassCard>
    );
}

// ── Custom Tooltip ───────────────────────────────────────────────────────────

function ChartTooltip({ active, payload, label }: any) {
    if (!active || !payload?.length) return null;
    const pt: PerformancePoint = payload[0]?.payload;
    return (
        <div className="bg-[#0f0f14] border border-white/[0.12] rounded-xl p-3 text-xs shadow-xl min-w-[160px]">
            <p className="text-white/50 mb-2">{label}</p>
            <p className="text-white font-bold text-base mb-1">{pt?.overall_score ?? payload[0]?.value}
                <span className="text-white/40 font-normal text-xs ml-1">/ 100</span>
            </p>
            {pt?.job_title && <p className="text-white/40 truncate">{pt.job_title}{pt.company ? ` · ${pt.company}` : ''}</p>}
        </div>
    );
}

// ── Dimension Bar Row ────────────────────────────────────────────────────────

function DimBar({ label, value, highlight }: { label: string; value: number; highlight?: 'best' | 'worst' }) {
    const pct = Math.min(100, Math.max(0, value));
    const barColor = highlight === 'best'
        ? 'bg-emerald-400'
        : highlight === 'worst'
        ? 'bg-red-400'
        : 'bg-white/70';
    return (
        <div className="flex items-center gap-3">
            <p className={`text-xs w-28 flex-shrink-0 ${highlight === 'best' ? 'text-emerald-400' : highlight === 'worst' ? 'text-red-400' : 'text-white/60'}`}>
                {label}
            </p>
            <div className="flex-1 h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all duration-700 ${barColor}`} style={{ width: `${pct}%` }} />
            </div>
            <p className={`text-xs font-semibold w-8 text-right ${highlight === 'best' ? 'text-emerald-400' : highlight === 'worst' ? 'text-red-400' : 'text-white/70'}`}>
                {value > 0 ? value : '–'}
            </p>
        </div>
    );
}

// ── Empty State ──────────────────────────────────────────────────────────────

function EmptyState() {
    return (
        <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-16 h-16 rounded-2xl bg-white/[0.04] border border-white/[0.08] flex items-center justify-center mb-4">
                <RiLineChartLine className="text-white/20" size={28} />
            </div>
            <h3 className="text-white/60 font-semibold mb-2">No data yet</h3>
            <p className="text-white/30 text-sm max-w-xs">
                Complete your first interview to start tracking performance trends across sessions.
            </p>
        </div>
    );
}

// ── AI Recommendations ───────────────────────────────────────────────────────

function buildRecommendations(data: PerformanceAnalytics): {
    priority: { title: string; detail: string; type: 'focus' | 'maintain' | 'next' }[];
    insights: string[];
} {
    const { summary, dimension_averages, performance_over_time } = data;
    const pts = performance_over_time;
    const priority: { title: string; detail: string; type: 'focus' | 'maintain' | 'next' }[] = [];
    const insights: string[] = [];

    // Weakest dimension
    if (summary.weakest_dimension) {
        const label = dimLabel(summary.weakest_dimension);
        const score = dimension_averages[summary.weakest_dimension as keyof DimensionScores] ?? 0;
        priority.push({
            type: 'focus',
            title: `Strengthen your ${label}`,
            detail: `Currently averaging ${score}/100. Focus on this first — it has the biggest impact on your overall score.`,
        });
    }

    // Best dimension — tell them to maintain
    if (summary.best_dimension) {
        const label = dimLabel(summary.best_dimension);
        const score = dimension_averages[summary.best_dimension as keyof DimensionScores] ?? 0;
        priority.push({
            type: 'maintain',
            title: `Keep up your ${label}`,
            detail: `Your strongest area at ${score}/100. Maintain this edge while building weaker dimensions.`,
        });
    }

    // Streak advice
    if ((summary.current_streak ?? 0) === 0) {
        priority.push({
            type: 'next',
            title: 'Resume your practice streak',
            detail: "Consistency beats intensity. Even one session per day compounds into significant improvement.",
        });
    } else if ((summary.current_streak ?? 0) >= 3) {
        insights.push(`You're on a ${summary.current_streak}-day streak — keep the momentum going.`);
    }

    // Trend insight
    if (pts.length >= 4) {
        const recent = pts.slice(-3).map(p => p.overall_score);
        const older  = pts.slice(-6, -3).map(p => p.overall_score);
        const recentAvg = recent.reduce((a, b) => a + b, 0) / recent.length;
        const olderAvg  = older.reduce((a, b) => a + b, 0) / older.length;
        const delta = Math.round(recentAvg - olderAvg);
        if (delta > 3) insights.push(`Your last 3 sessions averaged ${delta} points higher than the 3 before — you're trending up.`);
        else if (delta < -3) insights.push(`Your recent sessions dipped ${Math.abs(delta)} points vs the period before. Consider reviewing your approach.`);
    }

    // Session frequency
    if ((summary.sessions_this_week ?? 0) === 0) {
        priority.push({
            type: 'next',
            title: 'Schedule a session this week',
            detail: `You haven't practiced yet this week. Even a single session improves retention.`,
        });
    } else if ((summary.sessions_this_week ?? 0) >= 4) {
        insights.push(`${summary.sessions_this_week} sessions this week — impressive volume. Make sure to review your feedback between sessions.`);
    }

    // Score band advice
    if (summary.avg_score && summary.avg_score < 60) {
        priority.push({
            type: 'focus',
            title: 'Build fundamentals across all dimensions',
            detail: 'Focus on structure and clarity first — these lift every other score when you answer a question well.',
        });
    } else if (summary.avg_score && summary.avg_score >= 80) {
        priority.push({
            type: 'next',
            title: 'Push to elite performance',
            detail: 'You\'re in the top tier. Now focus on edge cases, advanced scenarios, and consistent professionalism under pressure.',
        });
    }

    return { priority: priority.slice(0, 4), insights };
}

function AIRecommendations({ data }: { data: PerformanceAnalytics }) {
    const { priority, insights } = buildRecommendations(data);

    const typeConfig = {
        focus:    { icon: <RiAlertLine size={14} />,         bg: 'bg-red-500/10 border-red-500/20',     text: 'text-red-400',     badge: 'Focus Area' },
        maintain: { icon: <RiTrophyLine size={14} />,        bg: 'bg-emerald-500/10 border-emerald-500/20', text: 'text-emerald-400', badge: 'Keep Going' },
        next:     { icon: <RiArrowRightLine size={14} />,    bg: 'bg-blue-500/10 border-blue-500/20',   text: 'text-blue-400',    badge: 'Next Step' },
    };

    return (
        <GlassCard>
            <div className="flex items-center gap-3 mb-5">
                <div className="w-8 h-8 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400 flex-shrink-0">
                    <RiSparklingLine size={16} />
                </div>
                <div>
                    <p className="text-white text-sm font-semibold">AI Recommendations</p>
                    <p className="text-white/30 text-xs">Based on your interview history</p>
                </div>
                <div className="ml-auto flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-purple-500/10 border border-purple-500/20">
                    <RiRobot2Line size={11} className="text-purple-400" />
                    <span className="text-purple-400 text-[10px] font-semibold uppercase tracking-wider">AI</span>
                </div>
            </div>

            <div className="space-y-3 mb-5">
                {priority.map((rec, i) => {
                    const cfg = typeConfig[rec.type];
                    return (
                        <div key={i} className={`flex gap-3 p-3.5 rounded-xl border ${cfg.bg}`}>
                            <div className={`mt-0.5 flex-shrink-0 ${cfg.text}`}>{cfg.icon}</div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-0.5">
                                    <p className={`text-xs font-semibold ${cfg.text}`}>{rec.title}</p>
                                    <span className={`text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-md border ${cfg.bg} ${cfg.text}`}>{cfg.badge}</span>
                                </div>
                                <p className="text-white/40 text-xs leading-relaxed">{rec.detail}</p>
                            </div>
                        </div>
                    );
                })}
            </div>

            {insights.length > 0 && (
                <div className="border-t border-white/[0.06] pt-4">
                    <p className="text-white/30 text-[10px] font-semibold uppercase tracking-wider mb-3 flex items-center gap-1.5">
                        <RiLightbulbLine size={11} /> Insights
                    </p>
                    <ul className="space-y-2">
                        {insights.map((insight, i) => (
                            <li key={i} className="flex items-start gap-2 text-xs text-white/50">
                                <RiCheckboxCircleLine size={13} className="text-white/20 flex-shrink-0 mt-0.5" />
                                {insight}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </GlassCard>
    );
}

// ── Improvements / Declines ──────────────────────────────────────────────────

function TrendAnalysis({ pts }: { pts: PerformancePoint[] }) {
    if (pts.length < 2) {
        return (
            <GlassCard>
                <p className="text-white/60 text-sm font-medium mb-1">Session-over-Session Trend</p>
                <p className="text-white/25 text-xs mb-4">Score delta per session (last 12)</p>
                <div className="flex flex-col items-center justify-center py-10 text-center">
                    <div className="w-12 h-12 rounded-2xl bg-white/[0.04] border border-white/[0.08] flex items-center justify-center mb-3">
                        <RiLineChartLine className="text-white/20" size={22} />
                    </div>
                    <p className="text-white/50 text-sm font-medium mb-1">Not enough data yet</p>
                    <p className="text-white/25 text-xs max-w-[220px]">
                        Complete one more interview to see how your score moves session-over-session.
                    </p>
                </div>
            </GlassCard>
        );
    }

    const withDelta = pts.map((pt, i) => ({
        ...pt,
        delta: i === 0 ? 0 : pt.overall_score - pts[i - 1].overall_score,
    })).slice(1);

    const improvements = withDelta.filter(p => p.delta > 0).sort((a, b) => b.delta - a.delta).slice(0, 3);
    const declines     = withDelta.filter(p => p.delta < 0).sort((a, b) => a.delta - b.delta).slice(0, 3);

    const barData = withDelta.slice(-12).map((p, i) => ({
        session: i + 1,
        delta: p.delta,
        score: p.overall_score,
        label: formatDate(p.date),
    }));

    return (
        <GlassCard>
            <p className="text-white/60 text-sm font-medium mb-1">Session-over-Session Trend</p>
            <p className="text-white/25 text-xs mb-4">Score delta per session (last 12)</p>

            <ResponsiveContainer width="100%" height={140}>
                <BarChart data={barData} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false} />
                    <XAxis dataKey="label" tick={{ fill: 'rgba(255,255,255,0.25)', fontSize: 10 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: 'rgba(255,255,255,0.25)', fontSize: 10 }} axisLine={false} tickLine={false} />
                    <Tooltip
                        content={({ active, payload }) => {
                            if (!active || !payload?.length) return null;
                            const d = payload[0].payload;
                            return (
                                <div className="bg-[#0f0f14] border border-white/10 rounded-xl p-2.5 text-xs shadow-xl">
                                    <p className="text-white/40 mb-1">{d.label}</p>
                                    <p className={`font-bold ${d.delta >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                        {d.delta > 0 ? '+' : ''}{d.delta} pts
                                    </p>
                                    <p className="text-white/50">Score: {d.score}</p>
                                </div>
                            );
                        }}
                    />
                    <Bar dataKey="delta" radius={[4, 4, 0, 0]}>
                        {barData.map((d, i) => (
                            <Cell key={i} fill={d.delta >= 0 ? 'rgba(52,211,153,0.7)' : 'rgba(248,113,113,0.7)'} />
                        ))}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>

            {(improvements.length > 0 || declines.length > 0) && (
                <div className="grid grid-cols-2 gap-4 mt-5 pt-4 border-t border-white/[0.06]">
                    <div>
                        <p className="text-emerald-400 text-[10px] font-semibold uppercase tracking-wider mb-2 flex items-center gap-1">
                            <RiArrowUpLine size={11} /> Top Improvements
                        </p>
                        <div className="space-y-1.5">
                            {improvements.map((p, i) => (
                                <div key={i} className="flex items-center justify-between text-xs">
                                    <span className="text-white/40 truncate flex-1 mr-2">{formatDate(p.date)}</span>
                                    <span className="text-emerald-400 font-semibold flex-shrink-0">+{p.delta}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div>
                        <p className="text-red-400 text-[10px] font-semibold uppercase tracking-wider mb-2 flex items-center gap-1">
                            <RiArrowDownLine size={11} /> Notable Declines
                        </p>
                        <div className="space-y-1.5">
                            {declines.map((p, i) => (
                                <div key={i} className="flex items-center justify-between text-xs">
                                    <span className="text-white/40 truncate flex-1 mr-2">{formatDate(p.date)}</span>
                                    <span className="text-red-400 font-semibold flex-shrink-0">{p.delta}</span>
                                </div>
                            ))}
                            {declines.length === 0 && (
                                <p className="text-white/20 text-xs">No declines — great consistency!</p>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </GlassCard>
    );
}

// ── Main Page ────────────────────────────────────────────────────────────────

export default function Analytics() {
    const [data, setData]       = useState<PerformanceAnalytics | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError]     = useState<string | null>(null);
    const [days, setDays]       = useState<number | undefined>(undefined);
    const [syncing, setSyncing] = useState(false);

    const load = async (d?: number, force = false) => {
        try {
            setSyncing(true);
            setError(null);
            const res = await analyticsService.getPerformance(d, force);
            setData(res);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to load analytics');
        } finally {
            setLoading(false);
            setSyncing(false);
        }
    };

    useEffect(() => { load(days); }, [days]);

    const pts  = data?.performance_over_time ?? [];
    const sum  = data?.summary;
    const dims = data?.dimension_averages;

    // Radar data
    const radarData = dims
        ? Object.entries(DIM_LABELS).map(([key, label]) => ({
            dimension: label,
            score: dims[key as keyof typeof DIM_LABELS] ?? 0,
            fullMark: 100,
        }))
        : [];

    // Chart data (last 20 sessions)
    const chartData = pts.slice(-20).map(p => ({
        ...p,
        date: formatDate(p.date),
    }));

    const hasData = pts.length > 0;

    return (
        <div className="space-y-6">

            {/* ── Header ── */}
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="text-2xl font-bold text-white">Performance Analytics</h2>
                    <p className="text-white/40 text-sm mt-0.5">Track your improvement across interview sessions</p>
                </div>
                <div className="flex items-center gap-2">
                    <div className="flex items-center bg-white/[0.04] border border-white/[0.08] rounded-xl p-1 gap-0.5">
                        {RANGE_OPTIONS.map(opt => (
                            <button
                                key={opt.label}
                                onClick={() => setDays(opt.days)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                                    days === opt.days
                                        ? 'bg-white text-black'
                                        : 'text-white/50 hover:text-white/80'
                                }`}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={() => load(days, true)}
                        disabled={syncing}
                        className="w-9 h-9 flex items-center justify-center rounded-xl bg-white/[0.04] border border-white/[0.08] text-white/40 hover:text-white transition-colors disabled:opacity-40"
                    >
                        <RiRefreshLine className={syncing ? 'animate-spin' : ''} size={16} />
                    </button>
                </div>
            </div>

            {/* ── Error ── */}
            {error && (
                <div className="px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                    {error}
                </div>
            )}

            {loading ? (
                <div className="space-y-4">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        {[0,1,2,3].map(i => <div key={i} className="h-28 rounded-2xl bg-white/[0.08] animate-pulse" />)}
                    </div>
                    <div className="h-64 rounded-2xl bg-white/[0.08] animate-pulse" />
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div className="h-72 rounded-2xl bg-white/[0.08] animate-pulse" />
                        <div className="h-72 rounded-2xl bg-white/[0.08] animate-pulse" />
                    </div>
                </div>
            ) : !hasData ? (
                <EmptyState />
            ) : (
                <>
                    {/* ── Stat Cards ── */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <StatCard
                            icon={<RiBarChartBoxLine size={16} />}
                            label="Sessions"
                            value={String(sum?.total_sessions ?? 0)}
                            sub={`${sum?.sessions_this_week ?? 0} this week`}
                        />
                        <StatCard
                            icon={<RiLineChartLine size={16} />}
                            label="Avg Score"
                            value={sum?.avg_score ? `${sum.avg_score}` : '–'}
                            sub="out of 100"
                        />
                        <StatCard
                            icon={sum?.improvement_pct != null && sum.improvement_pct >= 0
                                ? <RiArrowUpLine size={16} />
                                : <RiArrowDownLine size={16} />
                            }
                            label="Improvement"
                            value={sum?.improvement_pct != null
                                ? `${sum.improvement_pct > 0 ? '+' : ''}${sum.improvement_pct}%`
                                : '–'
                            }
                            sub="first vs recent sessions"
                            positive={sum?.improvement_pct != null
                                ? sum.improvement_pct >= 0
                                : null
                            }
                        />
                        <StatCard
                            icon={<RiFireLine size={16} />}
                            label="Streak"
                            value={`${sum?.current_streak ?? 0} day${(sum?.current_streak ?? 0) !== 1 ? 's' : ''}`}
                            sub="consecutive active days"
                            positive={sum?.current_streak ? sum.current_streak >= 3 : null}
                        />
                    </div>

                    {/* ── Score Over Time ── */}
                    <GlassCard>
                        <p className="text-white/60 text-sm font-medium mb-4">Score Over Time</p>
                        <ResponsiveContainer width="100%" height={220}>
                            <AreaChart data={chartData} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
                                <defs>
                                    <linearGradient id="scoreGrad" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%"  stopColor="#ffffff" stopOpacity={0.15} />
                                        <stop offset="95%" stopColor="#ffffff" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                                <XAxis
                                    dataKey="date"
                                    tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 11 }}
                                    axisLine={false}
                                    tickLine={false}
                                />
                                <YAxis
                                    domain={[0, 100]}
                                    tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 11 }}
                                    axisLine={false}
                                    tickLine={false}
                                />
                                <Tooltip content={<ChartTooltip />} />
                                <Area
                                    type="monotone"
                                    dataKey="overall_score"
                                    stroke="rgba(255,255,255,0.8)"
                                    strokeWidth={2}
                                    fill="url(#scoreGrad)"
                                    dot={{ fill: '#fff', r: 3, strokeWidth: 0 }}
                                    activeDot={{ fill: '#fff', r: 5, strokeWidth: 0 }}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </GlassCard>

                    {/* ── AI Recommendations + Trend Analysis ── */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <AIRecommendations data={data!} />
                        <TrendAnalysis pts={pts} />
                    </div>

                    {/* ── Radar + Dimension Bars ── */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

                        {/* Radar Chart */}
                        <GlassCard>
                            <p className="text-white/60 text-sm font-medium mb-2">Skill Dimensions</p>
                            <p className="text-white/25 text-xs mb-4">All-time averages across 7 dimensions</p>
                            <ResponsiveContainer width="100%" height={260}>
                                <RadarChart data={radarData} outerRadius="75%">
                                    <PolarGrid stroke="rgba(255,255,255,0.06)" />
                                    <PolarAngleAxis
                                        dataKey="dimension"
                                        tick={{ fill: 'rgba(255,255,255,0.45)', fontSize: 10 }}
                                    />
                                    <PolarRadiusAxis
                                        angle={90}
                                        domain={[0, 100]}
                                        tick={{ fill: 'rgba(255,255,255,0.2)', fontSize: 9 }}
                                        axisLine={false}
                                    />
                                    <Radar
                                        dataKey="score"
                                        stroke="rgba(255,255,255,0.7)"
                                        fill="rgba(255,255,255,0.08)"
                                        strokeWidth={1.5}
                                        dot={{ fill: '#fff', r: 3, strokeWidth: 0 }}
                                    />
                                </RadarChart>
                            </ResponsiveContainer>
                        </GlassCard>

                        {/* Dimension breakdown bars */}
                        <GlassCard>
                            <div className="flex items-center justify-between mb-4">
                                <p className="text-white/60 text-sm font-medium">Dimension Breakdown</p>
                            </div>
                            <div className="space-y-4">
                                {dims && Object.entries(DIM_LABELS).map(([key, label]) => (
                                    <DimBar
                                        key={key}
                                        label={label}
                                        value={dims[key as keyof typeof DIM_LABELS] ?? 0}
                                        highlight={
                                            key === sum?.best_dimension ? 'best'
                                            : key === sum?.weakest_dimension ? 'worst'
                                            : undefined
                                        }
                                    />
                                ))}
                            </div>
                            {(sum?.best_dimension || sum?.weakest_dimension) && (
                                <div className="mt-5 pt-4 border-t border-white/[0.06] grid grid-cols-2 gap-3">
                                    {sum?.best_dimension && (
                                        <div className="flex items-center gap-2 text-xs text-emerald-400">
                                            <RiTrophyLine size={14} />
                                            <span>Best: <strong>{dimLabel(sum.best_dimension)}</strong></span>
                                        </div>
                                    )}
                                    {sum?.weakest_dimension && (
                                        <div className="flex items-center gap-2 text-xs text-red-400">
                                            <RiAlertLine size={14} />
                                            <span>Focus: <strong>{dimLabel(sum.weakest_dimension)}</strong></span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </GlassCard>
                    </div>

                    {/* ── Session History Table ── */}
                    <GlassCard>
                        <p className="text-white/60 text-sm font-medium mb-4">
                            Session History
                            <span className="text-white/25 ml-2 font-normal">({pts.length} sessions)</span>
                        </p>
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                                <thead>
                                    <tr className="border-b border-white/[0.06]">
                                        <th className="text-left text-white/30 font-medium pb-2 pr-4">Date</th>
                                        <th className="text-left text-white/30 font-medium pb-2 pr-4">Role</th>
                                        <th className="text-center text-white/30 font-medium pb-2 px-2">Score</th>
                                        <th className="text-center text-white/30 font-medium pb-2 px-2">Δ</th>
                                        {Object.values(DIM_LABELS).map(l => (
                                            <th key={l} className="text-center text-white/20 font-medium pb-2 px-2 hidden md:table-cell">{l.slice(0,4)}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {[...pts].reverse().map((p, i, arr) => {
                                        const prev = arr[i + 1];
                                        const delta = prev ? p.overall_score - prev.overall_score : null;
                                        return (
                                            <tr key={p.interview_id} className={`border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors ${i === 0 ? 'bg-white/[0.02]' : ''}`}>
                                                <td className="py-2.5 pr-4 text-white/40">{formatDate(p.date)}</td>
                                                <td className="py-2.5 pr-4">
                                                    <p className="text-white/70 font-medium">{p.job_title}</p>
                                                    {p.company && <p className="text-white/30">{p.company}</p>}
                                                </td>
                                                <td className="py-2.5 px-2 text-center">
                                                    <span className={`inline-block px-2 py-0.5 rounded-lg border text-xs font-bold ${scoreBg(p.overall_score)} ${scoreColor(p.overall_score)}`}>
                                                        {p.overall_score}
                                                    </span>
                                                </td>
                                                <td className="py-2.5 px-2 text-center text-xs font-medium">
                                                    {delta !== null ? (
                                                        <span className={delta >= 0 ? 'text-emerald-400' : 'text-red-400'}>
                                                            {delta > 0 ? '+' : ''}{delta}
                                                        </span>
                                                    ) : (
                                                        <span className="text-white/20">—</span>
                                                    )}
                                                </td>
                                                {Object.keys(DIM_LABELS).map(key => (
                                                    <td key={key} className={`py-2.5 px-2 text-center hidden md:table-cell ${scoreColor(p.dimensions[key as keyof typeof p.dimensions])}`}>
                                                        {p.dimensions[key as keyof typeof p.dimensions] || '–'}
                                                    </td>
                                                ))}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </GlassCard>
                </>
            )}
        </div>
    );
}
